import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { aiAccept, getAIConfigs, aiGenerateStream, aiGenerateUploadStream, aiInspectUpload, getOllamaModels, getAIPromptSections, updateAIPromptSection, getAIPromptHistory, restoreAIPromptVersion, safeErrorMessage } from '../services/api';
import { useToast } from '../App';
import { useAuth } from '../contexts/AuthContext';
import { HiOutlineSparkles, HiOutlineCheckCircle, HiOutlineUpload, HiOutlineKey, HiOutlineDocumentText, HiOutlineCog } from 'react-icons/hi';
import { FiCpu, FiFile, FiType, FiShield, FiEdit3, FiSave, FiX, FiClock, FiRotateCcw, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { MODELS_BY_PROVIDER, PROVIDERS } from '../constants/aiModels';

export default function AIGenerate() {
  const { id } = useParams();
  const addToast = useToast();
  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState('');
  const [temperature, setTemperature] = useState(0.2);
  const [inputMode, setInputMode] = useState('text');
  const [textContent, setTextContent] = useState('');
  const [file, setFile] = useState(null);
  const [promptExtra, setPromptExtra] = useState('');
  const [loading, setLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  // Decoupage annonce par le serveur + statut de chaque feuille en direct
  const [sheets, setSheets] = useState(null);
  const [inspecting, setInspecting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [ollamaModels, setOllamaModels] = useState([]);
  const [ollamaModelsLoading, setOllamaModelsLoading] = useState(false);

  // System prompt state
  const { isAdmin } = useAuth();
  const [promptSections, setPromptSections] = useState([]);
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [historySection, setHistorySection] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadPromptSections = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await getAIPromptSections();
      setPromptSections(data.sections || []);
    } catch { /* ignore if not admin */ }
  }, [isAdmin]);

  useEffect(() => { loadPromptSections(); }, [loadPromptSections]);

  const handleSaveSection = async (sectionKey) => {
    setSavingPrompt(true);
    try {
      const { data } = await updateAIPromptSection(sectionKey, { content: editContent });
      setPromptSections((prev) => prev.map((s) => s.section_key === sectionKey ? data : s));
      setEditingSection(null);
      addToast('success', 'Section mise a jour avec succes.', 'Prompt IA');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      addToast('error', msg, 'Erreur');
    }
    setSavingPrompt(false);
  };

  const handleShowHistory = async (sectionKey) => {
    if (historySection === sectionKey) { setHistorySection(null); return; }
    setHistorySection(sectionKey);
    setHistoryLoading(true);
    try {
      const { data } = await getAIPromptHistory(sectionKey);
      setHistoryData(data.history || []);
    } catch { setHistoryData([]); }
    setHistoryLoading(false);
  };

  const handleRestoreVersion = async (sectionKey, version) => {
    try {
      const { data } = await restoreAIPromptVersion(sectionKey, version);
      setPromptSections((prev) => prev.map((s) => s.section_key === sectionKey ? data : s));
      setHistorySection(null);
      addToast('success', `Version ${version} restauree.`, 'Prompt IA');
    } catch (err) {
      addToast('error', err.response?.data?.detail || err.message, 'Erreur');
    }
  };

  const SECTION_ICONS = { role: '🎯', rules: '📋', output_format: '📐' };
  const SECTION_LABELS = { role: 'Role & Expertise', rules: 'Regles de Generation', output_format: 'Format de Sortie' };

  // Interroge le serveur sur le decoupage du fichier : combien de feuilles,
  // donc combien d'appels IA. Purement informatif, aucun modele n'est appele.
  const inspectFile = async (f) => {
    if (!/\.(xlsx|xlsm)$/i.test(f.name || '')) return;
    setInspecting(true);
    try {
      const formData = new FormData();
      formData.append('file', f);
      const { data } = await aiInspectUpload(id, formData);
      setSheets((data.segments || []).map((s) => ({ ...s, status: 'pending', exigences: 0 })));
    } catch {
      setSheets(null); // le fichier sera de toute facon revalide a la generation
    }
    setInspecting(false);
  };

  const fetchOllamaModels = async (savedModel) => {
    setOllamaModelsLoading(true);
    try {
      const { data } = await getOllamaModels();
      const models = data.models || [];
      setOllamaModels(models);
      // Auto-select: use saved model if available, otherwise first
      if (savedModel && models.includes(savedModel)) {
        setModel(savedModel);
      } else if (models.length > 0) {
        setModel(models[0]);
      }
    } catch {
      setOllamaModels([]);
    }
    setOllamaModelsLoading(false);
  };

  // Load saved configs and apply defaults
  useEffect(() => {
    getAIConfigs().then(({ data }) => {
      setSavedConfigs(data);
      // Auto-select the first configured provider, or keep openai
      const configuredProvider = data.find((c) => c.provider === 'openai') || data[0];
      if (configuredProvider) {
        const prov = configuredProvider.provider;
        setProvider(prov);
        if (configuredProvider.temperature !== undefined) setTemperature(configuredProvider.temperature);
        if (prov === 'ollama') {
          fetchOllamaModels(configuredProvider.model);
        } else {
          const models = MODELS_BY_PROVIDER[prov] || [];
          const savedModel = configuredProvider.model;
          if (savedModel && models.some((m) => m.value === savedModel)) {
            setModel(savedModel);
          } else if (models.length > 0) {
            setModel(models[0].value);
          }
        }
      }
    }).catch(() => { });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When provider changes, load its saved config
  const handleProviderChange = (newProvider) => {
    setProvider(newProvider);
    setOllamaModels([]);
    const config = savedConfigs.find((c) => c.provider === newProvider);
    if (newProvider === 'ollama') {
      setModel('');
      if (config) {
        setTemperature(config.temperature !== undefined ? config.temperature : 0.2);
        fetchOllamaModels(config.model || '');
      }
    } else {
      const models = MODELS_BY_PROVIDER[newProvider] || [];
      if (config) {
        const savedModel = config.model || '';
        if (savedModel && models.some((m) => m.value === savedModel)) {
          setModel(savedModel);
        } else {
          setModel(models.length > 0 ? models[0].value : '');
        }
        setTemperature(config.temperature !== undefined ? config.temperature : 0.2);
      } else {
        setModel(models.length > 0 ? models[0].value : '');
        setTemperature(0.2);
      }
    }
  };

  const currentProvider = PROVIDERS.find((p) => p.value === provider);
  const isOllama = provider === 'ollama';
  const currentModels = isOllama ? ollamaModels.map((m) => ({ value: m, label: m })) : (MODELS_BY_PROVIDER[provider] || []);
  const hasSavedKey = savedConfigs.some((c) => c.provider === provider);

  const handleGenerate = async () => {
    if (!isOllama && !hasSavedKey) {
      addToast('warning', 'Veuillez configurer une clé API dans Configuration.');
      return;
    }
    if (isOllama && !hasSavedKey) {
      addToast('warning', 'Veuillez configurer l\'URL Ollama dans Configuration.');
      return;
    }
    if (isOllama && !model) {
      addToast('warning', 'Veuillez sélectionner un modèle Ollama installé.');
      return;
    }

    setLoading(true);
    setElapsedTime(0);
    setError('');
    setResult({ exigences: [] });
    setSheets((prev) => prev && prev.map((s) => ({ ...s, status: 'pending', exigences: 0 })));
    const timer = setInterval(() => setElapsedTime((t) => t + 1), 1000);

    try {
      let response;
      if (inputMode === 'file' && file) {
        const formData = new FormData();
        formData.append('provider', provider);
        if (model) formData.append('model', model);
        formData.append('temperature', temperature.toString());
        formData.append('prompt_extra', promptExtra || '');
        formData.append('file', file);
        response = await aiGenerateUploadStream(id, formData);
      } else {
        response = await aiGenerateStream(id, {
          provider, model: model || null,
          temperature, content: textContent, prompt_extra: promptExtra,
        });
      }

      if (!response.ok) {
        if (response.status === 401) {
          window.location.href = '/login';
          return;
        }
        let errMsg = `Erreur serveur (${response.status})`;
        try {
          const errData = await response.json();
          errMsg = errData.detail || errMsg;
        } catch {}
        throw new Error(errMsg);
      }

      // Read SSE stream progressively
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let totalExigences = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Normalize \r\n to \n (Gemini SSE uses \r\n)
        buffer = buffer.replaceAll('\r\n', '\n');

        // Process complete SSE events (separated by double newline)
        while (buffer.includes('\n\n')) {
          const idx = buffer.indexOf('\n\n');
          const eventBlock = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);

          for (const line of eventBlock.split('\n')) {
            const trimmed = line.trim();
            if (!trimmed.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(trimmed.slice(6));
              if (event.type === 'exigence') {
                totalExigences++;
                setResult((prev) => ({
                  ...prev,
                  exigences: [...(prev?.exigences || []), event.data],
                }));
              } else if (event.type === 'segment') {
                setSheets((prev) => {
                  const next = (prev && prev.length === event.total)
                    ? [...prev]
                    : Array.from({ length: event.total }, (_, i) => ({
                        label: prev?.[i]?.label || `Feuille ${i + 1}`,
                        status: 'pending', exigences: 0,
                      }));
                  next[event.index - 1] = {
                    ...next[event.index - 1],
                    label: event.label,
                    status: event.status === 'start' ? 'running' : event.status,
                    exigences: event.exigences ?? next[event.index - 1]?.exigences ?? 0,
                  };
                  return next;
                });
              } else if (event.type === 'done') {
                setResult((prev) => ({ ...prev, _usage: event.usage }));
                if (event.segments_failed?.length) {
                  addToast('warning',
                    `Feuille(s) non générée(s) : ${event.segments_failed.join(', ')}. Les autres sont bien présentes.`,
                    'Génération partielle');
                }
              } else if (event.type === 'fallback') {
                addToast('warning',
                  `${event.original_model} indisponible — basculement automatique vers ${event.fallback_model}`,
                  'Fallback modèle');
              } else if (event.type === 'error') {
                throw new Error(event.message);
              }
            } catch (parseErr) {
              if (parseErr.message && !parseErr.message.includes('JSON')) {
                throw parseErr; // re-throw non-JSON errors (like our error events)
              }
            }
          }
        }
      }

      if (totalExigences > 0) {
        addToast('success', `${totalExigences} exigences générées.`, 'Génération terminée');
      } else {
        setError('Aucune exigence générée. Essayez avec un document plus détaillé ou un autre modèle.');
      }
    } catch (err) {
      const msg = err.message || 'Erreur inconnue';
      setError(msg);
      addToast('error', msg, 'Erreur');
      // Clear empty result on error
      setResult((prev) => (prev?.exigences?.length ? prev : null));
    }
    clearInterval(timer);
    setLoading(false);
  };

  const handleAccept = async () => {
    if (!result?.exigences) return;
    setAccepting(true);
    try {
      const { data } = await aiAccept(id, { exigences: result.exigences });
      addToast('success', `${data.requirements_added} exigences, ${data.test_cases_added} cas de test ajoutés.`, 'Ajouté au projet');
      setResult(null);
    } catch (err) {
      addToast('error', safeErrorMessage(err), 'Erreur');
    }
    setAccepting(false);
  };

  // Find the info for the current model
  const currentModel = isOllama ? null : currentModels.find((m) => m.value === model);
  const modelLabel = isOllama ? (model || 'Sélectionner un modèle') : (currentModel?.label || model || 'Par défaut');
  // Modeles de raisonnement : l'API impose sa temperature par defaut
  const ignoresTemperature = !!currentModel?.noTemperature;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2><HiOutlineSparkles className="me-2" style={{ color: 'var(--warning)' }} />Génération IA</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>Générez automatiquement des exigences et cas de test depuis vos documents</p>
        </div>
      </div>
      <div className="ai-card mb-4">
        <div className="ai-card-header">
          <h5><FiCpu className="me-2" />Configuration IA</h5>
        </div>
        <div className="card-body p-4">
          <span className="form-label d-block">Provider</span>
          <div className="ai-provider-selector mb-4">
            {PROVIDERS.map((p) => {
              const isConfigured = savedConfigs.some((c) => c.provider === p.value);
              const config = savedConfigs.find((c) => c.provider === p.value);
              return (
                <button type="button" key={p.value} className={`ai-provider-btn ${provider === p.value ? 'active' : ''}`}
                  onClick={() => handleProviderChange(p.value)}
                  style={{ background: 'none', border: 'none', font: 'inherit', color: 'inherit', cursor: 'pointer', padding: 0 }}>
                  <img src={p.logo} alt={p.label} style={{ width: 32, height: 32, objectFit: 'contain', marginBottom: '4px' }} />
                  <div>{p.label}</div>
                  {isConfigured && (
                    <div style={{ fontSize: '0.65rem', color: 'var(--green-600)', marginTop: '2px' }}>
                      <FiShield /> Configuré
                    </div>
                  )}
                  {isConfigured && config?.model && (
                    <div style={{ fontSize: '0.6rem', color: 'var(--gray-500)', marginTop: '1px', fontFamily: 'Consolas, monospace' }}>
                      {config.model}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {isOllama ? (
            hasSavedKey ? (
              <div className="d-flex align-items-center flex-wrap gap-2 p-2 mb-3" style={{ background: '#e0e7ff', borderRadius: 'var(--radius-sm)', fontSize: '0.78rem', color: '#3730a3' }}>
                <FiCpu />
                <span><strong>Ollama</strong> — LLM Local · Aucune clé API · T={temperature.toFixed(1)}</span>
                <Link to="/configuration" className="ms-auto" style={{ fontSize: '0.75rem' }}><HiOutlineCog className="me-1" />Configurer</Link>
              </div>
            ) : (
              <div className="d-flex align-items-center gap-2 p-2 mb-3" style={{ background: 'var(--yellow-100, #fff3cd)', borderRadius: 'var(--radius-sm)', fontSize: '0.78rem', color: 'var(--yellow-800, #856404)' }}>
                <HiOutlineKey />
                <span>URL Ollama non configurée.</span>
                <Link to="/configuration" className="ms-auto" style={{ fontSize: '0.75rem' }}><HiOutlineCog className="me-1" />Configurer</Link>
              </div>
            )
          ) : hasSavedKey ? (
            <div className="d-flex align-items-center flex-wrap gap-2 p-2 mb-3" style={{ background: 'var(--green-100)', borderRadius: 'var(--radius-sm)', fontSize: '0.78rem', color: 'var(--green-700)' }}>
              <FiShield />
              <span><strong>{currentProvider?.label}</strong> configuré · {ignoresTemperature ? 'température imposée par le modèle' : `T=${temperature.toFixed(1)}`}</span>
              <Link to="/configuration" className="ms-auto" style={{ fontSize: '0.75rem' }}><HiOutlineCog className="me-1" />Clé API</Link>
            </div>
          ) : (
            <div className="d-flex align-items-center gap-2 p-2 mb-3" style={{ background: 'var(--yellow-100, #fff3cd)', borderRadius: 'var(--radius-sm)', fontSize: '0.78rem', color: 'var(--yellow-800, #856404)' }}>
              <HiOutlineKey />
              <span>Aucune clé API configurée pour <strong>{currentProvider?.label}</strong>.</span>
              <Link to="/configuration" className="ms-auto" style={{ fontSize: '0.75rem' }}><HiOutlineCog className="me-1" />Configurer</Link>
            </div>
          )}

          <div className="mb-4">
            <span className="form-label d-block"><FiCpu className="me-1" />Modèle {isOllama && <span style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>(installés localement)</span>}</span>
            {isOllama ? (
              ollamaModelsLoading ? (
                <div style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>
                  <span className="spinner-border spinner-border-sm me-2" />Chargement des modèles Ollama...
                </div>
              ) : ollamaModels.length === 0 ? (
                <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
                  Aucun modèle disponible. Vérifiez qu'Ollama est démarré (<code>ollama serve</code>) et configurez l'URL dans{' '}
                  <Link to="/configuration">Configuration</Link>.
                </div>
              ) : (
                <div className="d-flex flex-wrap gap-2">
                  {ollamaModels.map((m) => (
                    <button key={m}
                      className={`btn btn-sm ${model === m ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setModel(m)}
                      style={{ fontFamily: 'Consolas, monospace', fontSize: '0.78rem' }}>
                      {m}
                    </button>
                  ))}
                </div>
              )
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {currentModels.map((m) => (
                  <button key={m.value}
                    className={`btn btn-sm ${model === m.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => !m.disabled && setModel(m.value)}
                    disabled={m.disabled}
                    style={m.disabled ? { opacity: 0.4, cursor: 'not-allowed', borderColor: 'var(--gray-200)' }
                      : model === m.value ? {} : { borderColor: 'var(--gray-300)' }}>
                    {m.label}
                    <span style={{ fontSize: '0.6rem', opacity: 0.7, marginLeft: '4px' }}>
                      {m.tier} · ~{m.speed}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* System Prompt (admin only) */}
          {isAdmin && promptSections.length > 0 && (
            <div className="mb-4">
              <div
                className="d-flex align-items-center justify-content-between"
                style={{ cursor: 'pointer', padding: '8px 0' }}
                onClick={() => setPromptExpanded(!promptExpanded)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPromptExpanded(!promptExpanded); } }}
                role="button"
                tabIndex={0}
              >
                <span className="form-label mb-0">
                  <FiShield className="me-1" style={{ color: 'var(--indigo-500)' }} />
                  System Prompt IA
                  <span style={{ fontSize: '0.7rem', color: 'var(--gray-500)', marginLeft: '8px' }}>
                    ({promptSections.length} sections · v{Math.max(...promptSections.map(s => s.version || 0))})
                  </span>
                </span>
                {promptExpanded ? <FiChevronUp /> : <FiChevronDown />}
              </div>

              {promptExpanded && (
                <div style={{ border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                  {promptSections.map((section) => (
                    <div key={section.section_key} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                      {/* Section header */}
                      <div className="d-flex align-items-center justify-content-between px-3 py-2"
                        style={{ background: 'var(--gray-50)', fontSize: '0.82rem' }}>
                        <span>
                          <span className="me-1">{SECTION_ICONS[section.section_key] || '📄'}</span>
                          <strong>{SECTION_LABELS[section.section_key] || section.title}</strong>
                          <span style={{ fontSize: '0.68rem', color: 'var(--gray-500)', marginLeft: '8px' }}>
                            v{section.version}
                            {section.updated_by && ` · ${section.updated_by}`}
                            {section.updated_at && ` · ${new Date(section.updated_at).toLocaleDateString('fr-FR')}`}
                          </span>
                        </span>
                        <span className="d-flex gap-1">
                          {editingSection === section.section_key ? (
                            <>
                              <button className="btn btn-sm btn-success py-0 px-2" onClick={() => handleSaveSection(section.section_key)} disabled={savingPrompt}
                                style={{ fontSize: '0.72rem' }}>
                                <FiSave className="me-1" />{savingPrompt ? '...' : 'Sauvegarder'}
                              </button>
                              <button className="btn btn-sm btn-outline-secondary py-0 px-2" onClick={() => setEditingSection(null)}
                                style={{ fontSize: '0.72rem' }}>
                                <FiX />
                              </button>
                            </>
                          ) : (
                            <>
                              <button className="btn btn-sm btn-outline-primary py-0 px-2"
                                onClick={() => { setEditingSection(section.section_key); setEditContent(section.content); }}
                                style={{ fontSize: '0.72rem' }}>
                                <FiEdit3 className="me-1" />Modifier
                              </button>
                              <button className="btn btn-sm btn-outline-secondary py-0 px-2"
                                onClick={() => handleShowHistory(section.section_key)}
                                style={{ fontSize: '0.72rem' }}>
                                <FiClock className="me-1" />Historique
                              </button>
                            </>
                          )}
                        </span>
                      </div>

                      {/* Section content */}
                      <div className="px-3 py-2">
                        {editingSection === section.section_key ? (
                          <textarea
                            className="form-control"
                            rows="10"
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            style={{ fontSize: '0.78rem', fontFamily: 'Consolas, monospace' }}
                          />
                        ) : (
                          <pre style={{
                            fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                            maxHeight: '200px', overflow: 'auto', margin: 0,
                            color: 'var(--gray-700)', fontFamily: 'Consolas, monospace',
                            background: 'transparent',
                          }}>
                            {section.content}
                          </pre>
                        )}
                      </div>

                      {/* History panel */}
                      {historySection === section.section_key && (
                        <div className="px-3 py-2" style={{ background: 'var(--indigo-50, #eef2ff)', borderTop: '1px solid var(--gray-200)' }}>
                          {historyLoading ? (
                            <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
                              <span className="spinner-border spinner-border-sm me-2" />Chargement...
                            </div>
                          ) : historyData.length === 0 ? (
                            <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>Aucun historique disponible.</div>
                          ) : (
                            <div style={{ maxHeight: '250px', overflow: 'auto' }}>
                              {historyData.map((h) => (
                                <div key={`${h.version}-${h.is_current}`} className="d-flex align-items-start justify-content-between mb-2 p-2"
                                  style={{ background: h.is_current ? 'var(--green-50, #f0fdf4)' : 'white', borderRadius: '6px', fontSize: '0.75rem' }}>
                                  <div style={{ flex: 1 }}>
                                    <span className="fw-bold">v{h.version}</span>
                                    {h.is_current && <span className="badge bg-success ms-1" style={{ fontSize: '0.6rem' }}>Actuelle</span>}
                                    <span style={{ color: 'var(--gray-500)', marginLeft: '8px' }}>
                                      {h.changed_by} · {h.changed_at ? new Date(h.changed_at).toLocaleString('fr-FR') : ''}
                                    </span>
                                    <pre style={{
                                      fontSize: '0.7rem', maxHeight: '80px', overflow: 'auto',
                                      margin: '4px 0 0', whiteSpace: 'pre-wrap', color: 'var(--gray-600)',
                                      fontFamily: 'Consolas, monospace', background: 'transparent',
                                    }}>
                                      {h.content.substring(0, 300)}{h.content.length > 300 ? '...' : ''}
                                    </pre>
                                  </div>
                                  {!h.is_current && (
                                    <button className="btn btn-sm btn-outline-warning py-0 px-2 ms-2"
                                      onClick={() => handleRestoreVersion(section.section_key, h.version)}
                                      style={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                                      <FiRotateCcw className="me-1" />Restaurer
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Security notice */}
                  <div className="px-3 py-2 d-flex align-items-center gap-2" style={{ background: 'var(--yellow-50, #fffbeb)', fontSize: '0.72rem', color: 'var(--yellow-700, #a16207)' }}>
                    <FiShield />
                    <span>Une couche de securite anti-injection est appliquee automatiquement (invisible et non modifiable).</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mb-4">
            <label className="form-label" htmlFor="field-prompt-extra"><HiOutlineDocumentText className="me-1" />Instructions supplementaires</label>
            <textarea id="field-prompt-extra" className="form-control" rows="2" value={promptExtra} onChange={(e) => setPromptExtra(e.target.value)}
              placeholder="Ex: Focuser sur les tests de securite, generer au moins 10 exigences..." />
          </div>

          <div className="divider"></div>

          <span className="form-label d-block">Source de données</span>
          <div className="d-flex gap-2 mb-3">
            <button className={`btn ${inputMode === 'text' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`} onClick={() => setInputMode('text')}>
              <FiType className="me-1" /> Texte
            </button>
            <button className={`btn ${inputMode === 'file' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`} onClick={() => setInputMode('file')}>
              <FiFile className="me-1" /> Fichier (PDF/Word/Excel/CSV/Texte)
            </button>
          </div>

          {inputMode === 'text' ? (
            <textarea className="form-control" rows="6" value={textContent} onChange={(e) => setTextContent(e.target.value)}
              placeholder="Collez ici le contenu à analyser..." />
          ) : (
            <div className="p-4 text-center" style={{ border: '2px dashed var(--gray-300)', borderRadius: 'var(--radius-md)', background: 'var(--gray-50)' }}>
              <HiOutlineUpload style={{ fontSize: '2rem', color: 'var(--gray-400)', marginBottom: '8px' }} />
              <div>
                <input type="file" className="form-control" accept=".pdf,.docx,.xlsx,.xlsm,.csv,.txt,.md" style={{ maxWidth: '400px', margin: '0 auto' }}
                  onChange={(e) => {
                    const f = e.target.files[0];
                    if (f?.size > 10 * 1024 * 1024) {
                      addToast('error', 'Fichier trop volumineux (max 10 Mo).'); e.target.value = ''; setFile(null); return;
                    }
                    setFile(f);
                    setSheets(null);
                    if (f) inspectFile(f);
                  }} />
              </div>
              <div className="mt-2 small text-muted">
                Formats acceptés : PDF, Word (.docx), Excel (.xlsx, .xlsm), CSV, texte (.txt, .md) — 10 Mo max.
              </div>
              {file && <div className="mt-2 small text-success"><FiFile className="me-1" />{file.name}</div>}
              {inspecting && (
                <div className="mt-2 small text-muted">
                  <output className="spinner-border spinner-border-sm me-2" aria-label="Analyse" />
                  Analyse des feuilles du classeur…
                </div>
              )}
              {sheets && sheets.length > 0 && (
                <div className="mt-3 text-start" style={{ maxWidth: '520px', margin: '0 auto' }}>
                  <div className="small mb-2" style={{ fontWeight: 600, color: 'var(--gray-700)' }}>
                    {sheets.length > 1
                      ? `${sheets.length} feuilles détectées — une génération IA par feuille`
                      : '1 feuille détectée — une seule génération'}
                  </div>
                  <div style={{ maxHeight: '210px', overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-sm)' }}>
                    {sheets.map((s, i) => (
                      <div key={`${s.label}-${i}`}
                        className="d-flex align-items-center gap-2 px-2 py-1"
                        style={{ fontSize: '0.75rem', borderBottom: i < sheets.length - 1 ? '1px solid var(--gray-100)' : 'none' }}>
                        <span style={{ minWidth: '2.2rem', color: 'var(--gray-400)' }}>{i + 1}/{sheets.length}</span>
                        <span className="flex-grow-1 text-truncate" title={s.label}
                          style={{ fontWeight: s.status === 'running' ? 600 : 400 }}>{s.label}</span>
                        {s.status === 'pending' && <span style={{ color: 'var(--gray-400)' }}>en attente</span>}
                        {s.status === 'running' && (
                          <span className="text-primary d-flex align-items-center gap-1">
                            <output className="spinner-border spinner-border-sm" aria-label="En cours"
                              style={{ width: '0.7rem', height: '0.7rem' }} />
                            en cours
                          </span>
                        )}
                        {s.status === 'done' && (
                          <span className="text-success">✓ {s.exigences} exigence{s.exigences > 1 ? 's' : ''}</span>
                        )}
                        {s.status === 'error' && <span className="text-danger">✗ échec</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-4 d-flex align-items-center gap-3">
            <button className="btn btn-success" onClick={handleGenerate} disabled={loading} style={{ minWidth: '200px' }}>
              {loading ? (
                <>
                  <output className="spinner-border spinner-border-sm me-2" aria-label="Chargement"></output>
                  Génération en cours...
                  {elapsedTime > 0 && (
                    <span className="ms-1">
                      ({Math.floor(elapsedTime / 60)}:{String(elapsedTime % 60).padStart(2, '0')})
                    </span>
                  )}
                </>
              ) : (
                <><HiOutlineSparkles className="me-1" /> Générer avec IA</>
              )}
            </button>
            {loading && sheets && sheets.length > 1 && (
              <span style={{ fontSize: '0.78rem', color: 'var(--gray-600)' }}>
                <FiFile className="me-1" />
                {sheets.filter((s) => s.status === 'done' || s.status === 'error').length}/{sheets.length} feuilles traitées
              </span>
            )}
            {model && !(loading && sheets && sheets.length > 1) && (
              <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                <FiCpu className="me-1" />{modelLabel}
                {!isOllama && currentModel?.tier && <span style={{ fontSize: '0.65rem', opacity: 0.7 }}> ({currentModel.tier})</span>}
                {isOllama && <span style={{ fontSize: '0.65rem', opacity: 0.7 }}> (Local)</span>}
                {' '}· {ignoresTemperature ? 'T=défaut' : `T=${temperature.toFixed(1)}`}
                {!isOllama && currentModel?.speed && <span style={{ opacity: 0.7 }}> · ~{currentModel.speed}</span>}
                {isOllama && <span style={{ opacity: 0.7 }}> · Gratuit</span>}
              </span>
            )}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {result?.exigences?.length > 0 && (
        <div className="ai-card">
          <div className="ai-card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <HiOutlineCheckCircle className="me-2" />
              Résultats ({result.exigences.length} exigences)
              {loading && <output className="spinner-border spinner-border-sm ms-2" aria-label="Chargement" />}
            </h5>
            <button className="btn btn-success btn-sm" onClick={handleAccept} disabled={accepting || loading} style={{ background: 'var(--green-500)', border: 'none' }}>
              {accepting ? 'Ajout...' : 'Accepter et ajouter au projet'}
            </button>
          </div>
          <div className="card-body p-4">
            {result.exigences.map((exg, idx) => (
              <div key={idx} className="card mb-3">
                <div className="card-body">
                  <h6 className="mb-2"><span className="req-id me-2">{exg.exg_id}</span>{exg.exigence}</h6>
                  <p className="small text-muted mb-2">{exg.desc_exigence}</p>
                  <div className="d-flex gap-2 mb-3">
                    <span className="meta-tag">{exg.type_exigence}</span>
                    <span className="meta-tag">Impact: {exg.impact}</span>
                    <span className="meta-tag">Prob: {exg.probabilite}</span>
                  </div>
                  {exg.source_text && (
                    <div className="mb-2 px-2 py-1" style={{ background: 'var(--navy-50, #f0f4fb)', borderLeft: '3px solid var(--navy-400)', borderRadius: '4px', fontSize: '0.75rem', color: 'var(--navy-700)' }}>
                      <strong style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Source PDF (exigence) :</strong>
                      <span className="ms-2" style={{ fontStyle: 'italic' }}>"{exg.source_text}"</span>
                    </div>
                  )}
                  {exg.cas_tests && (
                    <table className="table table-sm table-hover">
                      <thead><tr><th>ID</th><th>Cas de Test</th><th>Prérequis</th><th>Action</th><th>Résultat Attendu</th><th style={{ background: 'var(--navy-50, #f0f4fb)' }}>Source PDF</th></tr></thead>
                      <tbody>
                        {exg.cas_tests.map((ct, ctIdx) => (
                          <tr key={ctIdx}>
                            <td><span className="req-id" style={{ fontSize: '0.6rem' }}>{ct.ct_id}</span></td>
                            <td className="small">{ct.cas_test}</td>
                            <td className="small">{ct.prerequis}</td>
                            <td className="small action-text">{ct.action}</td>
                            <td className="small">{ct.resultat_attendu}</td>
                            <td className="small" style={{ background: 'var(--navy-50, #f0f4fb)', fontStyle: 'italic', color: 'var(--navy-700)', maxWidth: '200px' }}>
                              {ct.source_text || <span style={{ color: 'var(--gray-400)' }}>—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
